<i18n src="./translations"></i18n>

<template>
  <div class="wishlist-header" :data-preload="$options.name">
    <AccountHeader
      class="wishlist-header__title"
      :title="isSharedWishlist ? $t('shared_title') : $t('title')"
      :pretitle="isLoggedIn ? $t('pretitle') : ''"
    />
    <template v-if="!isLoggedIn">
      <span class="wishlist-header__subtitle">
        {{ $t('subtitle') }}
      </span>
      <Button
        class="wishlist-header__register-button"
        :title="$t('register')"
        modifier="primary"
        :href="frontFacingPath('customer.shop.login')"
      />
    </template>
    <template v-if="productCount">
      <div
        class="wishlist-header__utility utility qa-ActionsTile__contentInner"
      >
        <span class="utility__item-count">
          {{ $t('item_count', { '%count%': productCount }) }}
        </span>
        <div class="utility__button-group">
          <Button
            v-if="!isSharedWishlist"
            class="utility__empty-button qa-Wishlist__delete"
            icon="delete"
            :on-click="activateEmptyWishlistDialog"
          />
          <Button
            class="utility__share-button qa-Wishlist__share"
            :title="$t('cart.share')"
            icon="paper_plane"
            :on-click="activateShareWishlistDialog"
            modifier="lowercase"
          />
          <Button
            class="utility__send-button qa-Wishlist__cart"
            :title="$t('cart.send')"
            icon="cart"
            :modifiers="['primary', 'lowercase', 'reverse']"
            :on-click="activateAddAllDialog"
          />
        </div>
      </div>
      <ShareLinkDialog
        class="wishlist-header__share-dialog"
        :active="dialogState.share"
        :title="$t('cart.share')"
        type="wishlist"
        :link-id="idWishlist"
        @hide="hideWishlistShareDialog"
      />
      <Dialog
        ref="addAllDialog"
        class="wishlist-header__add-all-dialog"
        :title="$t('dialog.share.title')"
        :confirm-button="{
          title: $t('dialog.share.actions.accept'),
          onClick: () => sendAllToCart(),
        }"
        :cancel-button="{
          title: $t('cancel'),
          onClick: () => $refs.addAllDialog.hide(),
        }"
        :click-outside="true"
      >
        <div>{{ $t('dialog.share.message') }}</div>
      </Dialog>
      <Dialog
        ref="emptyWishlistDialog"
        class="wishlist-header__empty-dialog"
        :title="$t('dialog.empty.title')"
        :confirm-button="{
          title: $t('dialog.empty.actions.accept'),
          onClick: () => {
            emptyWishlist({ wishlistId: idWishlist });
            dialogState.empty = false;
          },
        }"
        :cancel-button="{
          title: $t('cancel'),
          onClick: () => {
            dialogState.empty = false;
            $refs.emptyWishlistDialog.hide();
          },
        }"
        @hide="dialogState.empty = false"
      >
        <div>
          {{ $t('dialog.empty.message') }}
        </div>
      </Dialog>
    </template>
    <div v-else class="wishlist-header__empty-wishlist empty-wishlist">
      <span class="empty-wishlist__message">
        {{ $t('empty_wishlist_message') }}
      </span>
      <Button
        class="empty-wishlist__start-page-button"
        :title="$t('start_page')"
        modifier="primary"
        href="/"
      />
    </div>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex';

import globalMixin from 'Libs/mixins/globalMixin';
import AccountHeader from 'Components/01-atoms/account-header/AccountHeader.vue';
import Button from 'Components/01-atoms/button/Button.vue';
import ShareLinkDialog from 'Src/components/02-molecules/share-link-dialog/ShareLinkDialog.vue';
import Dialog from 'Components/02-molecules/dialog/Dialog.vue';

export default {
  name: 'WishlistHeader',
  components: {
    ShareLinkDialog,
    AccountHeader,
    Button,
    Dialog,
  },
  mixins: [globalMixin],
  props: {
    isSharedWishlist: {
      type: Boolean,
      default: false,
    },
    productCount: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      dialogState: { share: false, addAll: false, empty: false },
      shareWishlistLink: '',
    };
  },
  computed: {
    ...mapState('core', {
      isLoggedIn: ({ requestData }) => requestData.isLoggedIn,
    }),
    ...mapState('wishlist', ['idWishlist']),
  },
  methods: {
    ...mapActions('wishlist', ['addAllToCart', 'emptyWishlist']),
    activateShareWishlistDialog() {
      this.dialogState.share = true;
    },
    activateAddAllDialog() {
      this.$refs.addAllDialog.show();
    },
    async sendAllToCart() {
      await this.addAllToCart({
        wishlistId: this.idWishlist,
      });
      this.dialogState.addAll = false;
    },
    activateEmptyWishlistDialog() {
      this.$refs.emptyWishlistDialog.show();
    },
    hideWishlistShareDialog() {
      this.dialogState.share = false;
    },
  },
};
</script>

<style lang="scss" scoped>
@import 'variables/breakpoints';

.wishlist-header__title {
  color: var(--color-secondary);
}

.wishlist-header__subtitle {
  display: block;
  margin-bottom: var(--space-2);
}

.wishlist-header__register-button {
  margin-bottom: var(--space-4);
}

.wishlist-header__utility {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  padding: 0 var(--space-2) var(--space-2) var(--space-2);
  border: 1px solid var(--color-alto);
}

.utility__item-count {
  font-weight: 700;
  color: var(--color-mine-shaft);
  font-size: var(--font-size-M);
  flex-grow: 1;
  margin-top: var(--space-2);
}

.utility__button-group {
  margin-top: var(--space-1);
  display: flex;
  flex-wrap: wrap;

  > button {
    margin-top: var(--space-1);
  }
}

.utility__empty-button {
  margin-right: var(--space-2);
  border: 1px solid var(--color-alto);

  &:hover {
    background: var(--color-alabaster);
  }

  ::v-deep path {
    fill: var(--color-primary) !important;
  }
}

.utility__share-button {
  color: var(--color-primary) !important;
  border: 1px solid var(--color-alto);
  border-radius: 3px;
  margin-right: var(--space-2);

  ::v-deep path {
    fill: var(--color-primary) !important;
  }

  &:hover {
    background: var(--color-alabaster);
  }
}

.empty-wishlist {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  padding: var(--space-7) var(--space-5);
  border: 1px solid var(--color-alto);
}

// [izerozlu] 530px as a breakpoint needed because
// when resolution is smaller then 530px header starts to break down.
// So instead of the regular 480px I've chose to use 530px.
@media (max-width: 530px) {
  .wishlist-header__register-button {
    width: 100%;
  }

  .utility__button-group {
    width: 100%;
    margin-top: var(--space-1);
  }

  .utility__share-button {
    flex-grow: 1;
    margin-right: 0;
  }

  .utility__send-button {
    flex-basis: 100%;
  }

  .empty-wishlist {
    padding: var(--space-4) var(--space-2);
  }

  .empty-wishlist__message {
    flex-basis: 100%;
    margin-bottom: var(--space-2);
  }

  .empty-wishlist__start-page-button {
    flex-grow: 1;
  }
}
</style>
